import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CircularProgress } from "@mui/material";

import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
} from "@mui/material";

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";

import Webcam from "react-webcam";
import { useTranslation } from "react-i18next";
import * as faceapi from "face-api.js";

import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  ControlpanelSuccess3thButton,
  GrayIconButton,
} from "../../../components/StyledButton";

import {
  CustomDialog,
  CustomTabPanel,
  CustomTabsV2,
} from "../../../components/StyledComponents";

import DropZoneInput from "../../../components/DropZoneInput";

import VideoIcon48pxSvg from "../../../assets/images/icons/VideoIcon48pxSvg";
import CaptureIcon16pxSvg from "../../../assets/images/icons/CaptureIcon16pxSvg";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";

const videoConstraints = {
  facingMode: "user",
};

const ImportDataDialog = ({
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const webcamRef = useRef(null);

  // lưu face trước đó
  const previousFaceRef = useRef(null);

  const [tabValue, setTabValue] =
    useState(0);

  const [capturedImage, setCapturedImage] =
    useState(null);

  const [cameraError, setCameraError] =
    useState(false);

  const [modelsLoaded, setModelsLoaded] =
    useState(false);

  const [faceValid, setFaceValid] =
    useState(false);

  const [faceMessage, setFaceMessage] =
    useState("");

  const [blinkDetected, setBlinkDetected] =
    useState(false);

  const [formData, setFormData] = useState({
    fileImport: null,
  });

  const [isDetecting, setIsDetecting] =
    useState(false);

  // =========================
  // TAB CHANGE
  // =========================

  const handleChangeTab = (
    event,
    newValue
  ) => {
    setTabValue(newValue);

    setBlinkDetected(false);

    setFaceValid(false);

    previousFaceRef.current = null;
  };

  // =========================
  // ACCESSIBILITY
  // =========================

  const a11yProps = (index) => {
    return {
      id: `user-tab-${index}`,
      "aria-controls": `user-tabpanel-${index}`,
    };
  };

  // =========================
  // FILE TO BASE64
  // =========================

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () =>
        resolve(reader.result);

      reader.onerror = (error) =>
        reject(error);
    });
  };

  // =========================
  // BLINK DETECTION
  // =========================

  const getEyeAspectRatio = (eye) => {
    const distance = (a, b) => {
      return Math.hypot(
        a.x - b.x,
        a.y - b.y
      );
    };

    const A = distance(eye[1], eye[5]);

    const B = distance(eye[2], eye[4]);

    const C = distance(eye[0], eye[3]);

    return (A + B) / (2.0 * C);
  };

  // =========================
  // VALIDATE IMAGE
  // =========================

  const validatePortraitImage = async (
    file
  ) => {
    try {
      const image =
        await faceapi.bufferToImage(file);

      const detections = await faceapi
        .detectAllFaces(
          image,
          new faceapi.TinyFaceDetectorOptions()
        )
        .withFaceLandmarks();

      // NO FACE
      if (detections.length === 0) {
        return {
          valid: false,
          message:
            t("common.noface") ||
            "Không tìm thấy khuôn mặt",
        };
      }

      // MULTIPLE FACE
      if (detections.length > 1) {
        return {
          valid: false,
          message:
            t("common.multipleface") ||
            "Chỉ được có 1 khuôn mặt",
        };
      }

      const face = detections[0];

      const box = face.detection.box;

      const imageWidth = image.width;

      const imageHeight = image.height;

      // FACE SIZE
      const faceArea =
        box.width * box.height;

      const totalArea =
        imageWidth * imageHeight;

      const ratio =
        faceArea / totalArea;

      if (ratio < 0.04) {
        return {
          valid: false,
          message:
            t("common.movecloser") ||
            "Khuôn mặt quá nhỏ",
        };
      }

      // CENTER CHECK
      const centerX =
        box.x + box.width / 2;

      const centerY =
        box.y + box.height / 2;

      const validX =
        centerX > imageWidth * 0.25 &&
        centerX < imageWidth * 0.75;

      const validY =
        centerY > imageHeight * 0.15 &&
        centerY < imageHeight * 0.85;

      if (!validX || !validY) {
        return {
          valid: false,
          message:
            t("common.centerface") ||
            "Khuôn mặt phải nằm giữa ảnh",
        };
      }

      return {
        valid: true,
        message:
          t("common.validface") ||
          "Khuôn mặt hợp lệ",
      };
    } catch (error) {
      console.error(error);

      return {
        valid: false,
        message:
          t("common.invalidimage") ||
          "Ảnh không hợp lệ",
      };
    }
  };

  // =========================
  // FILE CHANGE
  // =========================

  const handleFileChange = async (
    name,
    file
  ) => {
    if (!file) return;

    setIsDetecting(true);

    try {
      const result =
        await validatePortraitImage(file);

      setFaceValid(result.valid);

      setFaceMessage(result.message);

      if (!result.valid) {
        setFormData((prev) => ({
          ...prev,
          [name]: null,
        }));

        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } finally {
      setIsDetecting(false);
    }
  };

  const handleInputChange = async (e) => {
    const { name, files } = e.target;

    const file = files?.[0];

    if (!file) return;

    setIsDetecting(true);

    try {
      const result =
        await validatePortraitImage(file);

      setFaceValid(result.valid);

      setFaceMessage(result.message);

      if (!result.valid) {
        setFormData((prev) => ({
          ...prev,
          [name]: null,
        }));

        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } finally {
      setIsDetecting(false);
    }
  };

  // =========================
  // LOAD MODELS
  // =========================

  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL =
          "/portal/models";

        await faceapi.nets.tinyFaceDetector.loadFromUri(
          MODEL_URL
        );

        await faceapi.nets.faceLandmark68Net.loadFromUri(
          MODEL_URL
        );

        setModelsLoaded(true);
      } catch (error) {
        console.error(error);
      }
    };

    loadModels();
  }, []);

  // =========================
  // FACE DETECTION
  // =========================

  useEffect(() => {
    let interval;

    if (
      tabValue === 1 &&
      modelsLoaded &&
      webcamRef.current
    ) {
      interval = setInterval(async () => {
        try {
          const video =
            webcamRef.current?.video;

          if (
            !video ||
            video.readyState < 2 ||
            video.videoWidth <= 0 ||
            video.videoHeight <= 0
          ) {
            return;
          }

          const detections = await faceapi
            .detectAllFaces(
              video,
              new faceapi.TinyFaceDetectorOptions()
            )
            .withFaceLandmarks();

          // NO FACE
          if (detections.length === 0) {
            setFaceValid(false);

            setBlinkDetected(false);

            previousFaceRef.current = null;

            setFaceMessage(
              t("common.noface") ||
              "Không tìm thấy khuôn mặt"
            );

            return;
          }

          // MULTIPLE FACE
          if (detections.length > 1) {
            setFaceValid(false);

            setBlinkDetected(false);

            previousFaceRef.current = null;

            setFaceMessage(
              t("common.multipleface") ||
              "Chỉ được có 1 khuôn mặt"
            );

            return;
          }

          const face = detections[0];

          const box = face.detection.box;

          // =========================
          // CHECK NEW FACE
          // =========================

          const currentFace = {
            x: Math.round(box.x),
            y: Math.round(box.y),
            width: Math.round(box.width),
            height: Math.round(box.height),
          };

          if (previousFaceRef.current) {
            const prev =
              previousFaceRef.current;

            const diffX = Math.abs(
              prev.x - currentFace.x
            );

            const diffY = Math.abs(
              prev.y - currentFace.y
            );

            const diffW = Math.abs(
              prev.width -
              currentFace.width
            );

            const diffH = Math.abs(
              prev.height -
              currentFace.height
            );

            const isDifferentFace =
              diffX > 60 ||
              diffY > 60 ||
              diffW > 60 ||
              diffH > 60;

            if (isDifferentFace) {
              setBlinkDetected(false);

              setFaceValid(false);

              setFaceMessage(
                t("common.blink") ||
                "Vui lòng chớp mắt lại"
              );
            }
          }

          previousFaceRef.current =
            currentFace;

          const videoWidth =
            video.videoWidth;

          const videoHeight =
            video.videoHeight;

          // FACE SIZE
          const faceArea =
            box.width * box.height;

          const totalArea =
            videoWidth * videoHeight;

          const ratio =
            faceArea / totalArea;

          if (ratio < 0.05) {
            setFaceValid(false);

            setFaceMessage(
              t("common.movecloser") ||
              "Đưa mặt lại gần hơn"
            );

            return;
          }

          // CENTER CHECK
          const centerX =
            box.x + box.width / 2;

          const centerY =
            box.y + box.height / 2;

          const validX =
            centerX >
            videoWidth * 0.2 &&
            centerX <
            videoWidth * 0.8;

          const validY =
            centerY >
            videoHeight * 0.15 &&
            centerY <
            videoHeight * 0.85;

          if (!validX || !validY) {
            setFaceValid(false);

            setFaceMessage(
              t("common.centerface") ||
              "Đưa mặt vào giữa khung"
            );

            return;
          }

          // =========================
          // BLINK DETECTION
          // =========================

          const landmarks =
            face.landmarks;

          const leftEye =
            landmarks.getLeftEye();

          const rightEye =
            landmarks.getRightEye();

          const leftEAR =
            getEyeAspectRatio(leftEye);

          const rightEAR =
            getEyeAspectRatio(rightEye);

          const ear =
            (leftEAR + rightEAR) / 2;

          // mắt nhắm
          if (ear < 0.32) {
            setBlinkDetected(true);
          }

          // chưa blink
          if (!blinkDetected) {
            setFaceValid(false);

            setFaceMessage(
              t("common.blink") ||
              "Vui lòng chớp mắt để xác thực người thật"
            );

            return;
          }

          setFaceValid(true);

          setFaceMessage(
            t("common.validface") ||
            "Khuôn mặt hợp lệ"
          );
        } catch (error) {
          console.error(error);
        }
      }, 500);
    }

    return () => clearInterval(interval);
  }, [
    tabValue,
    modelsLoaded,
    t,
    blinkDetected,
  ]);

  // =========================
  // CAPTURE
  // =========================

  const capture = useCallback(() => {
    if (!faceValid) return;

    const imageSrc =
      webcamRef.current?.getScreenshot();

    if (imageSrc) {
      setCapturedImage(imageSrc);
    }
  }, [faceValid]);

  // =========================
  // RETAKE
  // =========================

  const handleRetake = () => {
    setCapturedImage(null);

    setBlinkDetected(false);

    setFaceValid(false);

    previousFaceRef.current = null;
  };

  // =========================
  // CONFIRM
  // =========================

  const handleConfirm = async () => {
    try {
      // UPLOAD
      if (
        tabValue === 0 &&
        formData.fileImport
      ) {
        const base64 =
          await convertFileToBase64(
            formData.fileImport
          );

        onConfirm?.(base64);
      }

      // WEBCAM
      if (
        tabValue === 1 &&
        capturedImage
      ) {
        onConfirm?.(capturedImage);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // =========================
  // CAMERA ACCESS
  // =========================

  useEffect(() => {
    if (tabValue !== 1) return;

    navigator.mediaDevices
      ?.getUserMedia({
        video: true,
      })
      .then(() => {
        setCameraError(false);
      })
      .catch(() => {
        setCameraError(true);
      });
  }, [tabValue]);

  return (
    <CustomDialog
      transitionDuration={300}
      maxWidth="md"
      open
      onClose={onClose}
      keepMounted
      fullWidth
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold">
              {t(
                "common.profilepicture"
              )}
            </p>
          </div>

          <GrayIconButton
            onClick={onClose}
          >
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <CustomTabsV2
          variant="fullWidth"
          indicatorColor="none"
          value={tabValue}
          onChange={handleChangeTab}
        >
          <Tab
            label={t("common.browser")}
            {...a11yProps(0)}
            disableRipple
          />

          <Tab
            label="Webcam"
            {...a11yProps(1)}
            disableRipple
          />
        </CustomTabsV2>

        {/* UPLOAD */}
        <CustomTabPanel
          value={tabValue}
          index={0}
        >
          <div className="px-1 pt-4">
            <div className="grid grid-cols-12 gap-4">
              <label className="col-span-12 text-sm font-medium">
                {t(
                  "common.profilepicture"
                )}
              </label>

              <div className="col-span-12">
                <DropZoneInput
                  className="w-full"
                  file={formData.fileImport}
                  handleInputChange={
                    handleInputChange
                  }
                  handleDropChange={
                    handleFileChange
                  }
                  name="fileImport"
                  accept="image/*"
                />
              </div>

              {isDetecting ? (
                <div className="col-span-12 flex items-center gap-2 text-sm text-[#6B7280]">
                  <CircularProgress size={18} />
                  <span>
                    {t("common.detectingface") ||
                      "Đang kiểm tra khuôn mặt..."}
                  </span>
                </div>
              ) : (
                faceMessage && (
                  <div
                    className={`
        col-span-12
        text-sm
        font-medium
        ${faceValid
                        ? "text-green-600"
                        : "text-red-600"
                      }
      `}
                  >
                    {faceMessage}
                  </div>
                )
              )}
            </div>
          </div>
        </CustomTabPanel>

        {/* WEBCAM */}
        <CustomTabPanel
          value={tabValue}
          index={1}
        >
          <div className="w-full flex justify-center py-4">
            {cameraError ? (
              <div className="w-full max-w-full flex flex-col items-center justify-center gap-4 px-6 text-center">
                <VideoIcon48pxSvg className="p-2 bg-[#F3F4F6] rounded-xl" />

                <div>
                  <p className="text-lg font-bold">
                    Không tìm thấy camera
                  </p>

                  <p className="text-sm text-[#6B7280] mt-1">
                    Vui lòng cấp quyền
                    camera
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-full flex flex-col items-center gap-4">
                {!capturedImage ? (
                  <>
                    <div
                      className={`
                        relative
                        w-full
                        max-w-full
                        overflow-hidden
                        rounded-xl
                        border-4
                        ${faceValid
                          ? "border-green-500"
                          : "border-red-500"
                        }
                      `}
                    >
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={
                          videoConstraints
                        }
                        onUserMediaError={() =>
                          setCameraError(
                            true
                          )
                        }
                        className="w-full h-auto object-cover aspect-video"
                      />

                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                        <div
                          className={`
                            px-3 py-1 rounded-full text-sm font-medium
                            ${faceValid
                              ? "bg-green-500 text-white"
                              : "bg-red-500 text-white"
                            }
                          `}
                        >
                          {faceMessage}
                        </div>
                      </div>
                    </div>

                    <ControlpanelPrimaryButton
                      disabled={!faceValid}
                      sx={{
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        padding: 0,
                      }}
                      onClick={capture}
                    >
                      <CaptureIcon16pxSvg />
                    </ControlpanelPrimaryButton>
                  </>
                ) : (
                  <>
                    <div className="relative w-full max-w-full">
                      <img
                        src={capturedImage}
                        alt="Captured"
                        className="w-full h-auto border rounded-xl object-cover aspect-video"
                      />

                      <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1">
                        <CheckCircleIcon />
                      </div>
                    </div>

                    <ControlpanelSuccess3thButton
                      sx={{
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        padding: 0,
                      }}
                      onClick={handleRetake}
                    >
                      <RefreshCircleIcon20pxSvgV2 />
                    </ControlpanelSuccess3thButton>
                  </>
                )}
              </div>
            )}
          </div>
        </CustomTabPanel>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton
            onClick={onClose}
          >
            <CloseIcon />

            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>

          <ControlpanelPrimaryButton
            disabled={
              tabValue === 0
                ? !formData.fileImport
                : !capturedImage
            }
            onClick={handleConfirm}
          >
            <PlayArrowIcon />

            <p>{t("button.execute")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default ImportDataDialog;